import React from "react";
import { Link } from "react-router-dom";
import { User, Zap, Star, Map, MessageSquare, Globe, Shield, Clock, ArrowRight } from "lucide-react";

const steps = [
  {
    num: "01",
    icon: User,
    title: "Create Your Sport Profile",
    desc: "Tell us which sports you play, your skill level, whether you prefer indoor or outdoor, and when you're available. Each sport gets its own profile with the right rating system (NTRP for tennis, Handicap for golf, DUPR for pickleball).",
    points: ["Set home city and travel cities", "Choose casual, competitive, or both", "Mark your availability windows", "State language and accessibility needs"],
  },
  {
    num: "02",
    icon: Zap,
    title: "Get Matched by Compatibility",
    desc: "Our matching engine considers six compatibility dimensions — not just sport. Skill fit, schedule overlap, venue proximity, format preference, social style, and safety signals all factor into your match quality score.",
    points: ["Skill-verified matching (not just self-declared)", "Real-time availability overlap", "Venue proximity calculation", "AI-powered match explanations"],
  },
  {
    num: "03",
    icon: MessageSquare,
    title: "Invite and Confirm",
    desc: "Send a play invite with a suggested time and venue. Your match receives your invite with your full profile. Accept, propose a different time, or pass. No ghosting — the system tracks reliability.",
    points: ["One-click play invites", "Venue suggestions based on both players", "Confirmation and reminder notifications", "Easy rescheduling"],
  },
  {
    num: "04",
    icon: Star,
    title: "Play, Rate, Rematch",
    desc: "Check in at the venue, complete the session, and leave a skill-verified review. Build your reliability score and rematch list. The more you play, the better your matches get.",
    points: ["Check-in confirmation", "Post-session skill-calibrated ratings", "Build your rematch list", "Streak and achievement badges"],
  },
];

const features = [
  { icon: Map, title: "Venue Intelligence", desc: "Browse 300+ mapped venues by sport. See surface type, lights, booking requirements, fees, and live reviews." },
  { icon: Globe, title: "Travel Mode", desc: "Activate a destination profile before you arrive. Find partners near your hotel and play the day you land." },
  { icon: Shield, title: "Trust & Safety", desc: "Public-venue-first recommendations for first meetups, check-in sharing, and skill verification protect every session." },
  { icon: Clock, title: "Last Minute Matching", desc: "Turn on \"available now\" or \"last minute today\" to find partners who are also free for a spontaneous session." },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="py-16 px-4 text-center">
        <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-3">How ActivitySports Works</p>
        <h1 className="text-5xl font-heading font-black text-brand-navy mb-4">From Profile to Court in Minutes</h1>
        <p className="text-gray-500 max-w-xl mx-auto">
          Stop relying on noisy group chats and skill mismatches. ActivitySports coordinates skill, schedule, venue, and format so every session starts right.
        </p>
      </div>

      {/* Steps */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        {steps.map((step, idx) => (
          <div key={step.num} className="flex gap-8 mb-12 items-start" data-testid={`step-${step.num}`}>
            <div className="flex-shrink-0 w-12 text-right">
              <span className="text-4xl font-heading font-black text-brand-green">{step.num}</span>
            </div>
            <div className="flex-1 pb-12 border-b border-gray-200 last:border-0">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-brand-green/10 rounded-lg flex items-center justify-center">
                  <step.icon size={18} className="text-brand-green" />
                </div>
                <h2 className="text-xl font-heading font-bold text-brand-navy">{step.title}</h2>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{step.desc}</p>
              <ul className="space-y-1.5">
                {step.points.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-lime flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Features grid */}
      <div id="travel" className="bg-surface/30 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-black text-brand-navy">Powerful Platform Features</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-xl p-6 flex gap-4 hover:border-brand-green/30 transition-colors">
                <div className="w-10 h-10 bg-brand-green/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-brand-green" />
                </div>
                <div>
                  <h3 className="font-bold text-brand-navy mb-2">{title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 px-4 text-center">
        <h2 className="text-4xl font-heading font-black text-brand-navy mb-4">
          Ready to Find Your <span className="text-brand-green">Match?</span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/register" className="px-8 py-3 bg-brand-orange text-white font-bold rounded-lg hover:bg-brand-orange-hover transition-colors" data-testid="how-it-works-register-cta">
            Join Free
          </Link>
          <Link to="/find-partners" className="px-8 py-3 border border-gray-200 text-brand-navy rounded-lg hover:border-brand-green/40 transition-colors flex items-center gap-2">
            Browse Players <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
